import { Component, computed, inject, input, output, signal, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NotificationService } from 'app/notification.service';
import { tableDts, hideCaptionBgBorder } from 'app/shared/design-tokens/table-dts';
import { FormatHelperService } from 'app/shared/helpers/format-helper.service';
import { LazyLoadVm } from 'app/shared/standalone/shared-table/lazy-load-vm.interface';
import { LazyTableService } from 'app/shared/standalone/shared-table/lazy-table.service';
import { UiSwitchModule } from 'ngx-ui-switch';
import { ButtonModule } from 'primeng/button';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputTextModule } from 'primeng/inputtext';
import { PopoverModule } from 'primeng/popover';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription, finalize } from 'rxjs';
import { BA_ContactVm } from 'app/shared/generated/BA/Models/Admin/BA_ContactVm';
import { SystemMessage } from 'app/core/system-message/system-message-service';
import { HttpClient, HttpParams } from '@angular/common/http';

@Component({
    selector: 'pcg-ba-contacts-list',
    standalone: true,
    imports: [
        TableModule
        , CommonModule
        , FormsModule
        , SkeletonModule
        , FloatLabelModule
        , InputTextModule
        , ButtonModule
        , UiSwitchModule
        , TooltipModule
        , PopoverModule
        , TagModule
    ],
    templateUrl: './contacts-list.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./contacts-list.component.scss']
})
export class BaContactsListComponent {

    lts = inject(LazyTableService);
    route = inject(ActivatedRoute);
    toastr = inject(NotificationService);
    modal = inject(NgbModal);
    http = inject(HttpClient);

    /** isSelected is true when contact is a part of the recon */
    isSelected = input<boolean>(false);
    /** Let parent element know to update child tables */
    update = output();

    //#region Lazy Service Data
    DEBOUNCE_TIME = 100;
    dataSource = 'api/BA/Contacts/GetContactsList';

    lazyLoad = signal<LazyLoadVm>(null);
    data = computed(() => this.isLoading() 
        ? FormatHelperService.GetSkeletonArray(20)
        : this.lazyLoad()?.data);

    totalRecords = computed(() => this.lazyLoad()?.totalRecords);
    tableInput = this.lts.setLazyTableInput(100, "name", 1);
    isLoading = signal<boolean>(true);
    isEmpty = computed(() => this.lazyLoad()?.data?.length === 0);
    //#endregion

    //#region Filters
    filters = signal<{
        searchText: string
        , isSelected: boolean
    }>(
        { searchText: null
        , isSelected: false }
    );
    //#endregion

    tableDts = {...tableDts, ...hideCaptionBgBorder};

    subscriptions: Subscription[] = [];

    ngOnInit() { 
        this.filters.update(o => ({...o, isSelected: this.isSelected()}));
        this.getContactList();
    }

    filterSearch(searchText: string) {
        this.filters.update(o => ({...o, searchText: searchText }));
        this.getContactList();
    }
    
    getContactList() {
        let timeout = setTimeout(() => { this.isLoading.set(true); }, this.DEBOUNCE_TIME)
        this.lts.getList(this.dataSource, this.tableInput, this.filters())
            .pipe(finalize(() => {this.isLoading.set(false); clearTimeout(timeout)}))
            .subscribe((data: LazyLoadVm) => { this.lazyLoad.set(data); });
    }

    //#region Toggle Contacts
    toggleContact(contact: BA_ContactVm) {
        this.toggleContactApi(contact.userId, !this.isSelected()).subscribe(
            r => {  
                if (!r.isSuccessful) { this.toastr.showMessage(r.message, r.messageClass); }
                if (r.isSuccessful) { this.update.emit(); } 
            }
        );
    }

    toggleContactApi(
        userId: number
        , isAdd: boolean
    ) {
        const source = isAdd 
            ? "api/BA/Contacts/AddContact" 
            : "api/BA/Contacts/RemoveContact";
        return this.http.post<SystemMessage>(source, userId);
    }

    toggleAll() {
        this.toggleAllApi(!this.isSelected()).subscribe(
            r => {
                if (!r.isSuccessful) { this.toastr.showMessage(r.message, r.messageClass); }
                if (r.isSuccessful) { this.update.emit(); }
            }
        );
    }

    toggleAllApi(isAdd: boolean) {
        const source = isAdd 
            ? "api/BA/Contacts/AddAllContacts" 
            : "api/BA/Contacts/RemoveAllContacts";
        const params = new HttpParams()
            .set('searchText', this.filters().searchText ?? ''); 
        return this.http.post<SystemMessage>(source, null, { params: params });
    }
    //#endregion
}